/**
 * V279: TipTap 기반 리치 텍스트 에디터
 *
 * 지원 기능:
 * - 굵게/기울임/밑줄, 글머리기호/번호 목록, 제목/본문 블록
 * - 글자 색상, 문단 정렬(왼쪽/가운데/오른쪽)
 * - 링크 삽입, 이미지 업로드(Firebase Storage), 표 삽입/편집
 * - 자연스러운 줄/문단 구분
 *
 * 보안:
 * - 저장 시점에 부모 컴포넌트가 sanitize() 호출 (FE 1차 방어)
 * - 서버 측 sanitize-html 추가 정화 (BE 2차 방어)
 * - 렌더 시점에 RichContentViewer가 DOMPurify로 3차 정화
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import { LINK_INPUT_RULE, validateLinkInput } from '../utils/announcementLink';

/** V70G8H: 링크 패널 입력 상태 (from/to = 대상 범위, originalText = 열 때 선택된 텍스트) */
interface LinkDraft {
  from: number;
  to: number;
  originalText: string;
  text: string;
  href: string;
  hasLink: boolean;
  error: string | null;
}

export interface RichTextEditorProps {
  /** HTML 문자열 (제어 컴포넌트) */
  value: string;
  /** 변경 콜백 — HTML 원본을 그대로 전달. 저장 직전 sanitize 필요 */
  onChange: (html: string) => void;
  /** 이미지 업로드 핸들러. File을 받아 최종 URL 반환 */
  onImageUpload: (file: File) => Promise<string>;
  placeholder?: string;
  disabled?: boolean;
}

const TOOLBAR_BTN: React.CSSProperties = {
  padding: '4px 8px',
  border: '1px solid var(--color-gray-300)',
  background: '#fff',
  borderRadius: '4px',
  fontSize: '12px',
  cursor: 'pointer',
  minWidth: '28px',
  height: '28px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const TOOLBAR_BTN_ACTIVE: React.CSSProperties = {
  ...TOOLBAR_BTN,
  background: '#2563eb',
  color: '#fff',
  borderColor: '#2563eb',
};

const COLOR_PALETTE = [
  '#000000', '#374151', '#6b7280', '#ef4444',
  '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6',
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onImageUpload,
  placeholder = '내용을 입력하세요',
  disabled = false,
}) => {
  const [isEmpty, setIsEmpty] = useState(true);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit v3 includes link/underline; keep defaults
        heading: { levels: [1, 2, 3] },
        // V70G8H: 작성 중 링크 클릭 시 바로 열지 않고 커서만 이동 (🔗 버튼으로 수정/제거)
        link: { openOnClick: false },
      }),
      // V279.1: width 속성을 가진 리사이즈 가능 Image
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              parseHTML: (element) => element.getAttribute('width') || element.style.width || null,
              renderHTML: (attributes) => {
                if (!attributes.width) return {};
                return { width: attributes.width, style: `width: ${attributes.width}; height: auto;` };
              },
            },
          };
        },
      }).configure({
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto; border-radius: 4px;',
        },
      }),
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    editable: !disabled,
    immediatelyRender: false,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
      setIsEmpty(ed.isEmpty);
    },
  });

  // 외부에서 value가 갱신된 경우 동기화 (수정 모드 초기 로딩 등)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    setIsEmpty(editor.isEmpty);
  }, [value, editor]);

  const handleImageButton = useCallback(async () => {
    if (!editor) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        alert('이미지는 10MB 이하만 업로드할 수 있습니다.');
        return;
      }
      try {
        const url = await onImageUpload(file);
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      } catch (err: any) {
        console.error('[RichTextEditor] 이미지 업로드 실패:', err);
        alert('이미지 업로드 실패: ' + (err?.message || '알 수 없는 오류'));
      }
    };
    input.click();
  }, [editor, onImageUpload]);

  // V70G8H: 링크 패널 — 표시 텍스트 + URL 지정, 기존 링크 수정/제거
  const [linkDraft, setLinkDraft] = useState<LinkDraft | null>(null);

  const handleLinkButton = useCallback(() => {
    if (!editor) return;
    const hasLink = editor.isActive('link');
    if (hasLink) editor.chain().focus().extendMarkRange('link').run();
    const { from, to } = editor.state.selection;
    const originalText = editor.state.doc.textBetween(from, to, '\n');
    setLinkDraft({
      from,
      to,
      originalText,
      text: originalText,
      href: hasLink ? ((editor.getAttributes('link').href as string | undefined) ?? '') : '',
      hasLink,
      error: null,
    });
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor || !linkDraft) return;
    const checked = validateLinkInput(linkDraft.href);
    if (!checked.ok) {
      setLinkDraft({ ...linkDraft, error: checked.reason });
      return;
    }
    const multiBlock = linkDraft.originalText.includes('\n');
    const text = multiBlock ? linkDraft.originalText : linkDraft.text;
    if (!text.trim()) {
      setLinkDraft({ ...linkDraft, error: '표시할 텍스트를 입력하세요.' });
      return;
    }
    const { from, to } = linkDraft;
    if (from !== to && text === linkDraft.originalText) {
      // 선택한 텍스트 그대로 — 서식(굵게 등) 유지하고 링크만 지정
      editor.chain().focus().setTextSelection({ from, to }).setLink({ href: checked.href }).run();
    } else {
      // 표시 텍스트 변경 또는 선택 없음 — 해당 범위를 링크 텍스트로 교체/삽입
      editor
        .chain()
        .focus()
        .insertContentAt({ from, to }, { type: 'text', text, marks: [{ type: 'link', attrs: { href: checked.href } }] })
        // 이어서 입력하는 글자는 링크에 포함하지 않음
        .unsetMark('link')
        .run();
    }
    setLinkDraft(null);
  }, [editor, linkDraft]);

  const removeLink = useCallback(() => {
    if (!editor || !linkDraft) return;
    editor.chain().focus().setTextSelection({ from: linkDraft.from, to: linkDraft.to }).unsetLink().run();
    setLinkDraft(null);
  }, [editor, linkDraft]);

  const handleInsertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) {
    return (
      <div style={{ minHeight: '240px', border: '1px solid var(--color-gray-300)', borderRadius: '8px', padding: '12px', color: '#9ca3af' }}>
        에디터를 불러오는 중…
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid var(--color-gray-300)', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
      <Toolbar
        editor={editor}
        onImageClick={handleImageButton}
        onLinkClick={handleLinkButton}
        onTableClick={handleInsertTable}
      />
      {linkDraft && (
        <LinkPanel
          draft={linkDraft}
          onChange={(patch) => setLinkDraft({ ...linkDraft, ...patch, error: null })}
          onApply={applyLink}
          onRemove={removeLink}
          onCancel={() => {
            setLinkDraft(null);
            editor.chain().focus().run();
          }}
        />
      )}
      <div
        style={{
          position: 'relative',
          padding: '12px 14px',
          minHeight: '240px',
          maxHeight: '600px',
          overflowY: 'auto',
          fontSize: '14px',
          lineHeight: 1.7,
          color: '#374151',
        }}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
        {isEmpty && (
          <div
            style={{
              position: 'absolute',
              top: '12px',
              left: '14px',
              color: '#9ca3af',
              pointerEvents: 'none',
              lineHeight: 1.7,
              margin: '0.4em 0',
            }}
          >
            {placeholder}
          </div>
        )}
      </div>
      <style>{`
        .ProseMirror { outline: none; min-height: 200px; }
        .ProseMirror p { margin: 0.4em 0; }
        .ProseMirror h1 { font-size: 1.6em; font-weight: 700; margin: 0.6em 0 0.3em; }
        .ProseMirror h2 { font-size: 1.3em; font-weight: 700; margin: 0.6em 0 0.3em; }
        .ProseMirror h3 { font-size: 1.1em; font-weight: 700; margin: 0.6em 0 0.3em; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.6em; margin: 0.4em 0; }
        .ProseMirror table { border-collapse: collapse; margin: 0.6em 0; width: 100%; table-layout: fixed; }
        .ProseMirror th, .ProseMirror td { border: 1px solid #d1d5db; padding: 6px 10px; min-width: 60px; vertical-align: top; }
        .ProseMirror th { background: #f3f4f6; font-weight: 600; }
        .ProseMirror a { color: #2563eb; text-decoration: underline; }
        .ProseMirror img { max-width: 100%; height: auto; border-radius: 4px; }
      `}</style>
    </div>
  );
};

interface LinkPanelProps {
  draft: LinkDraft;
  onChange: (patch: Partial<Pick<LinkDraft, 'text' | 'href'>>) => void;
  onApply: () => void;
  onRemove: () => void;
  onCancel: () => void;
}

const LINK_INPUT: React.CSSProperties = {
  flex: '1 1 200px',
  minWidth: 0,
  padding: '6px 8px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '4px',
  fontSize: '13px',
};

/** V70G8H: 링크 삽입/수정/제거 패널 (툴바 아래 인라인). Enter=적용, Esc=취소 — 상위 form 제출 방지 */
const LinkPanel: React.FC<LinkPanelProps> = ({ draft, onChange, onApply, onRemove, onCancel }) => {
  const multiBlock = draft.originalText.includes('\n');
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onApply();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };
  return (
    <div
      data-testid="rte-link-panel"
      style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px 10px', borderBottom: '1px solid var(--color-gray-200)', background: '#eff6ff' }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="rte-link-text" style={{ fontSize: '12px', color: '#374151', width: '64px' }}>표시 텍스트</label>
        <input
          id="rte-link-text"
          data-testid="rte-link-text"
          type="text"
          value={multiBlock ? draft.originalText.replace(/\n/g, ' ') : draft.text}
          disabled={multiBlock}
          onChange={(e) => onChange({ text: e.target.value })}
          onKeyDown={onKeyDown}
          placeholder="본문에 보일 텍스트"
          style={LINK_INPUT}
        />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="rte-link-url" style={{ fontSize: '12px', color: '#374151', width: '64px' }}>URL</label>
        <input
          id="rte-link-url"
          data-testid="rte-link-url"
          type="text"
          inputMode="url"
          value={draft.href}
          onChange={(e) => onChange({ href: e.target.value })}
          onKeyDown={onKeyDown}
          placeholder="https://… 또는 /tournament/apply"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          style={LINK_INPUT}
        />
      </div>
      <div style={{ fontSize: '11px', color: draft.error ? '#dc2626' : '#6b7280' }} data-testid="rte-link-message" role={draft.error ? 'alert' : undefined}>
        {draft.error || `${LINK_INPUT_RULE} 내부 경로는 같은 탭, 외부 주소는 새 탭으로 열립니다.`}
      </div>
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {draft.hasLink && (
          <button type="button" data-testid="rte-link-remove" onClick={onRemove} style={{ ...TOOLBAR_BTN, color: '#dc2626' }}>링크 제거</button>
        )}
        <button type="button" data-testid="rte-link-cancel" onClick={onCancel} style={TOOLBAR_BTN}>취소</button>
        <button type="button" data-testid="rte-link-apply" onClick={onApply} style={TOOLBAR_BTN_ACTIVE}>적용</button>
      </div>
    </div>
  );
};

interface ToolbarProps {
  editor: Editor;
  onImageClick: () => void;
  onLinkClick: () => void;
  onTableClick: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ editor, onImageClick, onLinkClick, onTableClick }) => {
  const btn = (active: boolean) => (active ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN);

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px',
      padding: '8px 10px',
      borderBottom: '1px solid var(--color-gray-200)',
      background: '#f9fafb',
    }}>
      <button type="button" style={btn(editor.isActive('bold'))} onClick={() => editor.chain().focus().toggleBold().run()} title="굵게"><b>B</b></button>
      <button type="button" style={btn(editor.isActive('italic'))} onClick={() => editor.chain().focus().toggleItalic().run()} title="기울임"><i>I</i></button>
      <button type="button" style={btn(editor.isActive('underline'))} onClick={() => editor.chain().focus().toggleUnderline().run()} title="밑줄"><u>U</u></button>

      <span style={{ width: '1px', background: '#e5e7eb', margin: '0 2px' }} />

      <button type="button" style={btn(editor.isActive('paragraph'))} onClick={() => editor.chain().focus().setParagraph().run()} title="본문">P</button>
      <button type="button" style={btn(editor.isActive('heading', { level: 1 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="제목 1">H1</button>
      <button type="button" style={btn(editor.isActive('heading', { level: 2 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="제목 2">H2</button>
      <button type="button" style={btn(editor.isActive('heading', { level: 3 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="제목 3">H3</button>

      <span style={{ width: '1px', background: '#e5e7eb', margin: '0 2px' }} />

      <button type="button" style={btn(editor.isActive('bulletList'))} onClick={() => editor.chain().focus().toggleBulletList().run()} title="글머리기호">•</button>
      <button type="button" style={btn(editor.isActive('orderedList'))} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="번호목록">1.</button>

      <span style={{ width: '1px', background: '#e5e7eb', margin: '0 2px' }} />

      <button type="button" style={btn(editor.isActive({ textAlign: 'left' }))} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="왼쪽 정렬">⟸</button>
      <button type="button" style={btn(editor.isActive({ textAlign: 'center' }))} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="가운데 정렬">≡</button>
      <button type="button" style={btn(editor.isActive({ textAlign: 'right' }))} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="오른쪽 정렬">⟹</button>

      <span style={{ width: '1px', background: '#e5e7eb', margin: '0 2px' }} />

      {COLOR_PALETTE.map((color) => (
        <button
          key={color}
          type="button"
          title={`색상 ${color}`}
          onClick={() => editor.chain().focus().setColor(color).run()}
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: '1px solid #d1d5db',
            background: color,
            cursor: 'pointer',
            padding: 0,
          }}
        />
      ))}
      <button
        type="button"
        title="색상 제거"
        onClick={() => editor.chain().focus().unsetColor().run()}
        style={{ ...TOOLBAR_BTN, fontSize: '11px' }}
      >×</button>

      <span style={{ width: '1px', background: '#e5e7eb', margin: '0 2px' }} />

      <button type="button" style={btn(editor.isActive('link'))} onClick={onLinkClick} title="링크">🔗</button>
      <button type="button" style={TOOLBAR_BTN} onClick={onImageClick} title="이미지">🖼</button>
      <button type="button" style={TOOLBAR_BTN} onClick={onTableClick} title="표 삽입">⊞</button>

      {editor.isActive('image') && (
        <>
          <span style={{ width: '1px', background: '#e5e7eb', margin: '0 2px' }} />
          <span style={{ alignSelf: 'center', fontSize: '11px', color: '#6b7280' }}>이미지:</span>
          {(['25%', '50%', '75%', '100%'] as const).map((w) => (
            <button
              key={w}
              type="button"
              style={TOOLBAR_BTN}
              onClick={() => editor.chain().focus().updateAttributes('image', { width: w }).run()}
              title={`이미지 너비 ${w}`}
            >{w}</button>
          ))}
          <button
            type="button"
            style={TOOLBAR_BTN}
            onClick={() => editor.chain().focus().updateAttributes('image', { width: null }).run()}
            title="원본 크기"
          >원본</button>
        </>
      )}

      {editor.isActive('table') && (
        <>
          <span style={{ width: '1px', background: '#e5e7eb', margin: '0 2px' }} />
          <button type="button" style={TOOLBAR_BTN} onClick={() => editor.chain().focus().addRowAfter().run()} title="아래에 행 추가">+행</button>
          <button type="button" style={TOOLBAR_BTN} onClick={() => editor.chain().focus().addColumnAfter().run()} title="오른쪽에 열 추가">+열</button>
          <button type="button" style={TOOLBAR_BTN} onClick={() => editor.chain().focus().deleteRow().run()} title="행 삭제">−행</button>
          <button type="button" style={TOOLBAR_BTN} onClick={() => editor.chain().focus().deleteColumn().run()} title="열 삭제">−열</button>
          <button type="button" style={TOOLBAR_BTN} onClick={() => editor.chain().focus().deleteTable().run()} title="표 삭제">표×</button>
        </>
      )}
    </div>
  );
};

export default RichTextEditor;
